import WindowIds from "../../models/layout/windowIds";
import { Tooltip } from "@mui/material";
import featureService from "../../services/featureService";
import Feature from "../../models/feature";
import ProjectInfo from "../../models/projectInfo";
import MapLayer from "../../models/mapLayer";
import { useContext, useEffect, useState } from "react";
import ChartEdit from "./ChartEdit";
import { Chart } from "../../models/chart";
import TableColum from "../../models/tableColumn";
import tableService from "../../services/tableService";
import UserContext from "../../contexts/userContext";
import User from "../../models/user";
import chartService from "../../services/chartService";
import ChartsChanged from "./chartsChanged";
import ChartShareIcon from "./ChartShareIcon";
import WindowVisibilityChanged from "../common/windowVisibilityChanged";

const Charts: React.FC<{
  project: ProjectInfo;
  charts: Chart[];
  onChartsChanged: ChartsChanged;
  thematicMapLayers: MapLayer[];
  getWindowVisibility: any;
  onWindowVisibilityChanged: WindowVisibilityChanged;
}> = ({
  project,
  charts,
  onChartsChanged,
  thematicMapLayers,
  getWindowVisibility,
  onWindowVisibilityChanged,
}) => {
  const user = useContext<User | undefined>(UserContext);

  const [showEditChart, setShowEditChart] = useState<boolean>(false);

  const [chart, setChart] = useState<Chart>(new Chart());

  const [tableColumns, setTableColumns] = useState<TableColum[]>([]);

  useEffect(() => {
    const init = async () => {
      const { data: tableColumns } = await tableService.getTableColumns(
        project.idProject,
      );

      setTableColumns(tableColumns);
    };

    init();
  }, [project]);

  const onCancelEditChart = () => {
    setShowEditChart(false);
  };

  const closeOpenPanels = async () => {
    setShowEditChart(false);
  };

  const onNewChart = () => {
    closeOpenPanels();

    const newChart = new Chart();
    newChart.idProject = project.idProject;

    newChart.isVisible = true;

    if (charts.length > 0) {
      const indexes: number[] = charts.map((c) => c.index);
      newChart.index = Math.max(...indexes) + 1;
    } else {
      newChart.index = 1;
    }

    if (user) {
      newChart.idUser = user.idUser;
    }

    setChart(newChart);

    setShowEditChart(true);
  };

  const onEditChart = (event: any, chart: Chart) => {
    event.stopPropagation();
    setChart(chart);

    closeOpenPanels();

    setShowEditChart(true);
  };

  const onCreateChart = async (chart: Chart) => {
    if (!user?.isDemoUser) {
      await chartService.createChart(chart);
    }

    await onChartsChanged(chart, true, false, false, undefined);

    closeOpenPanels();
  };

  const onUpdateChart = async (chart: Chart, oldChart: Chart) => {
    if (!user?.isDemoUser) {
      await chartService.updateChart(chart);
    }

    await onChartsChanged(chart, false, true, false, oldChart);

    closeOpenPanels();
  };

  const onDeleteChart = async (chart: Chart) => {
    if (!user?.isDemoUser) {
      await chartService.deleteChart(chart.idChart);
    }

    await onChartsChanged(chart, false, false, true, undefined);

    closeOpenPanels();
  };

  const onChartVisibilityChanged = (chart: Chart) => {
    const toggleIsVisible = !chart.isVisible;

    onWindowVisibilityChanged(
      WindowIds.getChartId(chart.chartName),
      toggleIsVisible,
      { chart },
    );
  };

  return (
    <div className={showEditChart ? "d-flex" : ""}>
      <div style={{ minWidth: "250px" }}>
        {featureService.isFeatureEnabled(Feature.Charts, project) && (
          <div className="dropdown-section">
            <div className="mb-1 d-flex justify-content-between align-items-center">
              <h4 style={{ margin: "0px" }}>Charts</h4>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onNewChart}
              >
                New Chart
              </button>
            </div>
            <ul
              style={{
                listStyle: "none",
                margin: "0px",
                padding: "0px",
              }}
            >
              {charts.map((chart) => (
                <li key={`chartList_${chart.chartName}`}>
                  <button
                    className={
                      "dropdown-item-li-button dropdown-item" +
                      (chart.isVisible ? " active" : "")
                    }
                    type="button"
                    onClick={() => onChartVisibilityChanged(chart)}
                  >
                    <div className="d-flex justify-content-between">
                      <div className="d-flex align-items-center">
                        <div>{chart.chartName}</div>
                        <ChartShareIcon chart={chart} />
                      </div>
                      <div
                        style={{ marginLeft: "15px" }}
                        onClick={(event) => onEditChart(event, chart)}
                      >
                        <Tooltip
                          className="ms-2 align-self-center"
                          title="Chart Settings"
                        >
                          <i className="navbar-nav-fa fa fa-cog fa-lg"></i>
                        </Tooltip>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {featureService.isFeatureEnabled(Feature.SummaryCharts, project) && (
          <div className="dropdown-section mt-2">
            <div className="mb-1 d-flex justify-content-between align-items-center">
              <h4 style={{ margin: "0px" }}>Summary Charts</h4>
              {/* Dummy Button UI Space Holder */}
              <button
                type="button"
                className="btn btn-sm btn-primary"
                style={{ visibility: "hidden" }}
              >
                New Chart
              </button>
            </div>
            <ul
              style={{
                listStyle: "none",
                margin: "0px",
                padding: "0px",
              }}
            >
              {thematicMapLayers.map((thematicMapLayer) => (
                <li key={`summaryChartList_${thematicMapLayer.layerName}`}>
                  <button
                    className={
                      "dropdown-item-li-button dropdown-item" +
                      (getWindowVisibility(
                        WindowIds.getSummaryChartId(thematicMapLayer.layerName),
                      )?.isVisibile
                        ? " active"
                        : "")
                    }
                    type="button"
                    onClick={() =>
                      onWindowVisibilityChanged(
                        WindowIds.getSummaryChartId(thematicMapLayer.layerName),
                        !getWindowVisibility(
                          WindowIds.getSummaryChartId(
                            thematicMapLayer.layerName,
                          ),
                        )?.isVisibile,
                      )
                    }
                  >
                    <div className="d-flex">
                      <div>{thematicMapLayer.layerName} Summary</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {showEditChart && (
        <div className="editor-separator ms-2">
          <ChartEdit
            project={project}
            tableColumns={tableColumns}
            chart={
              user?.isPortalAdmin &&
              chart.parentChart &&
              chart.parentChart.isDefault
                ? chart.parentChart
                : chart
            }
            isNew={chart.chartName === ""}
            onCreateChart={onCreateChart}
            onUpdateChart={onUpdateChart}
            onDeleteChart={onDeleteChart}
            onCancel={onCancelEditChart}
          />
        </div>
      )}
    </div>
  );
};

export default Charts;
